import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CasinoIcon from "@mui/icons-material/Casino";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, CircularProgress, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { isInputFieldDisabled } from "../../../../../utils/validation/imageValidation";
import InputPreset from "./InputPreset";
import { PromptEnhancementModal } from "../../../components/PromptEnhancementModal";
import { useUploadImageContext } from "../../../../../context/juno/UploadImageContext";

interface PromptActionsProps {
  localPrompt: string | any; // 実行時に文字列以外が渡される可能性があるため、安全な処理が必要
  aiEngine: string;
  menu?: string;
  rotationDegree: number;
  generatingPrompt: boolean;
  onGenerateSampleText: () => void;
  onUpdatePrompt: () => void;
  onDelete: () => void;
  setLocalPrompt: (prompt: string) => void;
  showDiceOnly?: boolean;
  showImproveOnly?: boolean;
  showPresetOnly?: boolean;
  showDeleteOnly?: boolean;
  disabled?: boolean;
}


const PromptActions: React.FC<PromptActionsProps> = ({
  localPrompt,
  aiEngine,
  menu = "txt2img",
  rotationDegree,
  generatingPrompt,
  onGenerateSampleText,
  onUpdatePrompt,
  onDelete,
  setLocalPrompt,
  showDiceOnly,
  showImproveOnly,
  showPresetOnly,
  showDeleteOnly,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { image } = useUploadImageContext();
  const [enhancementModalOpen, setEnhancementModalOpen] = useState(false);
  
  // localPromptを安全な文字列として取得
  const safeLocalPrompt = typeof localPrompt === 'string' ? localPrompt : String(localPrompt || '');
  
  // プロンプト入力が無効かどうかを判定
  const isPromptDisabled = isInputFieldDisabled(aiEngine as any, menu as any);

  // 特定のアイコンのみを表示するモード
  if (showDiceOnly) {
    return (
      <CasinoIcon
        sx={{
          transform: `rotate(${rotationDegree}deg)`,
          transition: "transform 1s ease-in-out",
        }}
      />
    );
  }

  if (showImproveOnly) {
    return (
      <>
        <Tooltip title={t("juno.enhancement.tooltip")}>
          <span>
            <IconButton onClick={() => setEnhancementModalOpen(true)} disabled={isPromptDisabled}>
              <AutoFixHighIcon />
            </IconButton>
          </span>
        </Tooltip>
        <PromptEnhancementModal
          open={enhancementModalOpen}
          onClose={() => setEnhancementModalOpen(false)}
          onInsert={setLocalPrompt}
          initialPrompt={safeLocalPrompt}
          aiEngine={aiEngine}
          mode="image"
          promptImage={image}
        />
      </>
    );
  }

  if (showDeleteOnly) {
    return (
      <Tooltip title={t("common.delete")}>
        <span>
          <IconButton onClick={onDelete} disabled={isPromptDisabled}>
            <DeleteIcon />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  if (showPresetOnly) {
    return <InputPreset prompt={safeLocalPrompt} setPrompt={setLocalPrompt} disabled={disabled} />;
  }

  // 通常モード（すべてのアクションを表示）
  const renderActionButton = () => {
    const isEmpty = safeLocalPrompt.trim().length === 0;

    if (isEmpty) {
      return (
        <Tooltip title={t("juno.history.dice")}>
          <span>
            <IconButton onClick={onGenerateSampleText} disabled={isPromptDisabled}>
              <CasinoIcon
                sx={{
                  transform: `rotate(${rotationDegree}deg)`,
                  transition: "transform 1s ease-in-out",
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      );
    }

    return (
      <Tooltip title={t("juno.enhancement.tooltip")}>
        <span>
          <IconButton onClick={() => setEnhancementModalOpen(true)} disabled={isPromptDisabled}>
            <AutoFixHighIcon />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", flexDirection: "row" }}>
      {renderActionButton()}
      <InputPreset prompt={safeLocalPrompt} setPrompt={setLocalPrompt} disabled={isPromptDisabled} />
      <Tooltip title={t("common.delete")}>
        <span>
          <IconButton onClick={onDelete} disabled={isPromptDisabled}>
            <DeleteIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Divider orientation="vertical" sx={{ height: "28px", mx: 0.5 }} variant="middle" />

      <Box sx={{ display: { xs: "none", sm: "block" }, alignItems: "center" }}>
        <Typography variant="body2" color="secondary" ml={1}>
          @{t(`juno.input.engine.${aiEngine}`)}
        </Typography>
      </Box>

      <PromptEnhancementModal
        open={enhancementModalOpen}
        onClose={() => setEnhancementModalOpen(false)}
        onInsert={setLocalPrompt}
        initialPrompt={safeLocalPrompt}
        aiEngine={aiEngine}
        mode="image"
        promptImage={image}
      />
    </Box>
  );
};

export default PromptActions;
